// UsersTab.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, Tab, Modal } from 'react-bootstrap';
import moment from 'moment-timezone';
import Swal from 'sweetalert2';
import debounce from 'lodash/debounce';
import { useNavigate } from 'react-router-dom';
import 'moment/locale/ru';
import 'moment/locale/uk';
import 'moment/locale/ko';
import 'moment/locale/fr';
import {formatDate} from "../utils/dateUtils";
import api from "../api";

function UsersTab({ loadingBarRef, isActive }) {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('usersInfo');
    const [searchTerm, setSearchTerm] = useState('');
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [banUserId, setBanUserId] = useState('');
    const [banDuration, setBanDuration] = useState('');
    const [banReason, setBanReason] = useState('');
    const [bannedUsers, setBannedUsers] = useState([]);
    const [filteredBannedUsers, setFilteredBannedUsers] = useState([]);
    const [searchBannedTerm, setSearchBannedTerm] = useState('');
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const userListRef = useRef(null);
    const [total, setTotal] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const navigate = useNavigate(); //fix issue with error answer loadUsers

    const loadUsers = useCallback(async () => {
        if (loading || !hasMore) return;

        if (loadingBarRef.current) {
            loadingBarRef.current.continuousStart();
        }
        setLoading(true);

        try {
            const response = await api.get(`${process.env.REACT_APP_API_URL}/api/admin/users`, {
                params: { page, limit: 10 },
                headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
            });
            const newUsers = response.data.users;
            setTotal(response.data.total);
            setUsers(prevUsers => [...prevUsers, ...newUsers]);
            setFilteredUsers(prevUsers => [...prevUsers, ...newUsers]);
            setHasMore(newUsers.length === 10);
            setPage(prevPage => prevPage + 1);
        } catch (error) {
            Swal.fire({
                text: t('admin.errorFetchingUsers'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('admin.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            }).then(() => {
                setActiveTab('banUser');
                setLoading(false);
                setHasMore(false);
            });
        } finally {
            setLoading(false);
            if (loadingBarRef.current) {
                loadingBarRef.current.complete();
            }
        }
    }, [loadingBarRef, page, loading, hasMore]);

    const loadBannedUsers = useCallback(() => {
        if (loadingBarRef.current) {
            loadingBarRef.current.continuousStart();
        }

        api.get(`${process.env.REACT_APP_API_URL}/api/admin/banned-users`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        })
            .then(response => {
                setBannedUsers(response.data);
                setFilteredBannedUsers(response.data);
            })
            .catch(error => console.error('Error fetching banned users', error))
            .finally(() => {
                if (loadingBarRef.current) {
                    loadingBarRef.current.complete();
                }
            });
    }, [loadingBarRef]);

    const searchUsers = useCallback(async (term) => {
        if (term.length < 3) {
            setIsSearching(false);
            setFilteredUsers(users);
            return;
        }

        if (loadingBarRef.current) {
            loadingBarRef.current.continuousStart();
        }
        setLoading(true);

        try {
            const response = await api.get(`${process.env.REACT_APP_API_URL}/api/admin/users/search`, {
                params: { term },
                headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
            });
            setFilteredUsers(response.data);
            setIsSearching(true);
        } catch (error) {
            console.error('Error searching users', error);
        } finally {
            setLoading(false);
            if (loadingBarRef.current) {
                loadingBarRef.current.complete();
            }
        }
    }, [loadingBarRef, users]);

    const debouncedSearch = useCallback(debounce((term) => searchUsers(term), 300), [searchUsers]);

    const handleSearchChange = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        setBanUserId(term);
        debouncedSearch(term);
    };

    const handleBanSearchChange = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        setBanUserId(term);
    };

    useEffect(() => {
        if (isActive && activeTab === 'usersInfo' && users.length === 0 && !isSearching) {
            setUsers([]);
            setFilteredUsers([]);
            setPage(1);
            setHasMore(true);
            loadUsers();
        } else if (isActive && activeTab === 'bannedUsers') {
            loadBannedUsers();
        }
    }, [isActive, activeTab, loadUsers, loadBannedUsers, users.length, isSearching]);

    const handleScroll = useCallback(() => {
        const userList = userListRef.current;
        if (
            isActive &&
            userList &&
            userList.scrollHeight - userList.scrollTop <= userList.clientHeight + 50 &&
            !loading &&
            activeTab === 'usersInfo' &&
            !isSearching
        ) {
            loadUsers();
        }
    }, [isActive, loading, loadUsers, activeTab, isSearching]);

    useEffect(() => {
        const userList = userListRef.current;
        if (isActive && userList) {
            userList.addEventListener('scroll', handleScroll);
            return () => userList.removeEventListener('scroll', handleScroll);
        }
    }, [isActive, handleScroll]);

    const handleUserClick = (user) => {
        setSelectedUser(user);
        setShowModal(true);
    };

    const confirmBanUser = () => {
        if (!searchTerm) {
            Swal.fire({
                text: t('admin.steamIdRequired'),
                icon: 'warning',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ffcc00',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('admin.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            return;
        }

        if (!banDuration || isNaN(banDuration) || parseInt(banDuration) <= 0) {
            Swal.fire({
                text: t('admin.durationRequired'),
                icon: 'warning',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ffcc00',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('admin.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            return;
        }

        if (!banReason) {
            Swal.fire({
                text: t('admin.reasonRequired'),
                icon: 'warning',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ffcc00',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('admin.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            return;
        }

        Swal.fire({
            text: t('admin.confirmBanUser'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('admin.ok'),
            cancelButtonText: t('auth.cancel'),
            background: '#1a1a1a',
            color: '#b3b3b3',
            iconColor: '#ffcc00',
            customClass: {
                popup: 'swal2-dark-popup',
                confirmButton: 'swal2-dark-button',
                cancelButton: 'swal2-dark-button'
            }
        }).then((result) => {
            if (result.isConfirmed) {
                handleBanUserConfirmed();
            }
        });
    };

    const handleBanUserConfirmed = () => {
        if (loadingBarRef.current) {
            loadingBarRef.current.continuousStart();
        }
        api.post(`${process.env.REACT_APP_API_URL}/api/admin/ban-user`, {
            userId: searchTerm,
            duration: banDuration,
            reason: banReason
        }, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        })
            .then(() => {
                Swal.fire({
                    text: t('admin.userBannedSuccessfully'),
                    icon: 'success',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('admin.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
                setBanUserId('');
                setBanDuration('');
                setBanReason('');
                setSearchTerm('');
                setUsers([]);
                setPage(1);
                setHasMore(true);
                loadUsers();
            })
            .catch((error) => {
                const errorMessage = error.response?.data?.error || t('admin.failedToBanUser');
                Swal.fire({
                    text: errorMessage,
                    icon: 'error',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#ff0000',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('admin.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            })
            .finally(() => {
                if (loadingBarRef.current) {
                    loadingBarRef.current.complete();
                }
            });
    };

    const confirmUnban = (steamId) => {
        Swal.fire({
            text: t('admin.confirmUnban'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('admin.ok'),
            cancelButtonText: t('auth.cancel'),
            background: '#1a1a1a',
            color: '#b3b3b3',
            iconColor: '#ff0000',
            customClass: {
                popup: 'swal2-dark-popup',
                confirmButton: 'swal2-dark-button',
                cancelButton: 'swal2-dark-button'
            }
        }).then((result) => {
            if (result.isConfirmed) {
                handleUnbanUser(steamId);
            }
        });
    };

    const handleUnbanUser = (steamId) => {
        if (loadingBarRef.current) {
            loadingBarRef.current.continuousStart();
        }
        api.post(`${process.env.REACT_APP_API_URL}/api/admin/unban-user`, {
            userId: steamId
        }, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        })
            .then(() => {
                Swal.fire({
                    text: t('admin.userUnbannedSuccessfully'),
                    icon: 'success',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('admin.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
                loadBannedUsers();
            })
            .catch(() => {
                Swal.fire({
                    text: t('admin.failedToUnbanUser'),
                    icon: 'error',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#ff0000',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('admin.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            })
            .finally(() => {
                if (loadingBarRef.current) {
                    loadingBarRef.current.complete();
                }
            });
    };

    const getStatusText = (state) => {
        switch (state) {
            case 0: return t('admin.offline');
            case 1: return t('admin.online');
            case 2: return t('admin.busy');
            case 3: return t('admin.away');
            case 4: return t('admin.snooze');
            case 5: return t('admin.lookingToTrade');
            case 6: return t('admin.lookingToPlay');
            default: return t('admin.unknown');
        }
    };

    const calculateBanDuration = (startDateStr, endDateStr) => {
        if (!startDateStr || !endDateStr) return t('admin.unknown');
        const startDate = new Date(startDateStr);
        const endDate = new Date(endDateStr);
        const diffMs = endDate - startDate;
        const totalSeconds = Math.floor(diffMs / 1000);
        const days = Math.floor(totalSeconds / (3600 * 24));
        const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        let durationStr = '';
        if (days > 0) {
            durationStr += days + ' ' + t('admin.days') + ' ';
        }
        if (hours > 0) {
            durationStr += hours + ' ' + t('admin.hours') + ' ';
        }
        if (minutes > 0 || durationStr === '') {
            durationStr += minutes + ' ' + t('admin.minutes');
        }
        return durationStr;
    };

    const handleBanButtonClick = () => {
        const steamId = selectedUser?.steam_id || '';
        setSearchTerm(steamId);
        setBanUserId(steamId);
        setActiveTab('banUser');
        setShowModal(false);
    };

    const isOnline = (lastActivityString) => {
        if (!lastActivityString) return false;
        const lastActivityMoment = moment(lastActivityString);
        const now = moment();
        const diffInMinutes = now.diff(lastActivityMoment, 'minutes');
        return diffInMinutes <= 10;
    };

    const getSteamStatusClass = (state) => {
        switch (state) {
            case 0: return 'steam-offline';
            case 1: return 'steam-online';
            case 2: return 'steam-busy';
            case 3: return 'steam-away';
            case 4: return 'steam-snooze';
            case 5: return 'steam-looking-to-trade';
            case 6: return 'steam-looking-to-play';
            default: return 'steam-unknown';
        }
    };

    const formatRoles = (roles = []) => {
        if (!roles || roles.length === 0) return ['unknown'];

        const formattedRoles = new Set();

        if (roles.includes('ROLE_ADMIN') || roles.includes('ROLE_SUPER_ADMIN')) {
            formattedRoles.add('admin');
        }
        if (roles.includes('ROLE_MODERATOR')) {
            formattedRoles.add('moderator');
        }
        if (roles.includes('ROLE_USER') && !roles.includes('ROLE_ADMIN') && !roles.includes('ROLE_SUPER_ADMIN') && !roles.includes('ROLE_MODERATOR')) {
            formattedRoles.add('user');
        }

        return Array.from(formattedRoles);
    };

    return (
        <div className="bans-tab">
            <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                id="bans-management-tabs"
                className="mb-3"
            >
                <Tab eventKey="usersInfo" title={t('admin.usersInfo')}>
                    <div className="users-tab">
                        <div className="total-users">
                            <p>{t('admin.usersCount')}: {total}</p>
                        </div>
                        <div className="user-info-section">
                            <div id="user-inputs">
                                <input
                                    type="text"
                                    placeholder={t('admin.searchByNameOrSteamId')}
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                />
                            </div>
                        </div>
                        <div className="user-list" ref={userListRef}>
                            {filteredUsers.map(user => (
                                <div
                                    key={user.steam_id}
                                    className="user-item"
                                    onClick={() => handleUserClick(user)}
                                >
                                    <div style={{display: 'flex', alignItems: 'center'}}>
                                        <img
                                            src={user.profileIcon || '/favicon.ico'}
                                            alt={user.name}
                                            style={{
                                                width: '40px',
                                                height: '40px',
                                                borderRadius: '50%',
                                                marginRight: '10px'
                                            }}
                                        />
                                        <div>
                                            <span style={{color: 'var(--sp-text-muted)', textDecoration: 'none', fontWeight: 'bold'}}>
                                                {user.name}
                                            </span>
                                            <span style={{color: 'var(--sp-text-faint)', marginLeft: '5px'}}>({user.steam_id})</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {loading && <div>Loading...</div>}
                        </div>
                    </div>
                </Tab>
                <Tab eventKey="banUser" title={t('admin.banUserTab')}>
                    <div className="ban-user-tab">
                        <div className="ban-user-section">
                            <h3>{t('admin.banUser')}</h3>
                            <div id="ban-user-inputs">
                                <input
                                    type="text"
                                    placeholder={t('admin.steamId')}
                                    value={searchTerm}
                                    onChange={handleBanSearchChange}
                                />
                                <input
                                    type="text"
                                    placeholder={t('admin.banDurationHours')}
                                    value={banDuration}
                                    onChange={(e) => setBanDuration(e.target.value)}
                                />
                                <textarea
                                    value={banReason}
                                    onChange={(e) => setBanReason(e.target.value)}
                                    maxLength={500}
                                    placeholder={t('admin.reason')}
                                />
                            </div>
                            <button onClick={confirmBanUser}>{t('admin.banUserButton')}</button>
                        </div>
                    </div>
                </Tab>
                <Tab eventKey="bannedUsers" title={t('admin.bannedUsersTab')}>
                    <div className="banned-users-tab">
                        <h3>{t('admin.bannedUsers')}</h3>
                        <input
                            type="text"
                            placeholder={t('admin.searchByNameOrSteamId')}
                            value={searchBannedTerm}
                            onChange={handleBanSearchChange}
                        />
                        <div className="user-list">
                            {filteredBannedUsers.map(user => (
                                <div key={user.steam_id} className="user-item">
                                    <div>
                                        <span>{user.name} ({user.steam_id})</span>
                                        <p>{t('admin.bannedUntil')}: {formatDate(user.ban_end_date, { format: 'LLL' })}</p>
                                        <p>{t('admin.bannedFor')}: {calculateBanDuration(user.ban_start_date, user.ban_end_date)}</p>
                                        <p>{t('admin.reason')}: {user.ban_reason}</p>
                                    </div>
                                    <button onClick={() => confirmUnban(user.steam_id)}>
                                        {t('admin.unbanUserButton')}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </Tab>
            </Tabs>

            {selectedUser && (
                <Modal
                    show={showModal}
                    onHide={() => setShowModal(false)}
                    dialogClassName="admin-user-modal"
                >
                    <Modal.Header closeButton style={{ borderBottom: '1px solid var(--sp-border)' }}>
                        <Modal.Title>{selectedUser?.name}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="user-modal">
                            <div className="user-modal__avatar">
                                <img
                                    src={
                                        selectedUser?.profileIconFile ||
                                        selectedUser?.profileIcon ||
                                        '/favicon.ico'
                                    }
                                    alt="Avatar"
                                    className="avatar-image"
                                />
                            </div>
                            <div className="user-modal__section">
                                <h3>{t('admin.mainInfo')}</h3>
                                <p><strong>{t('admin.id')}:</strong> {selectedUser?.id}</p>
                                <p><strong>{t('admin.steamId')}:</strong> {selectedUser?.steam_id}</p>
                                <p>
                                    <strong>{t('admin.personaname')}: </strong> {selectedUser?.profileData?.personaname}
                                </p>
                                <p><strong>{t('admin.timezone')}:</strong> {selectedUser?.timezone}</p>
                                <p>
                                    <strong>{t('admin.steamStatus')}:</strong>{' '}
                                    <span className={getSteamStatusClass(selectedUser?.profileData?.personastate)}>
                                        {getStatusText(selectedUser?.profileData?.personastate)}
                                    </span>
                                </p>
                                <p>
                                    <strong>{t('admin.profileUrl')}:</strong>{' '}
                                    <a
                                        href={selectedUser?.profileData?.profileurl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="profile-url"
                                    >
                                        {t('admin.viewProfile')}
                                    </a>
                                </p>
                            </div>
                            <div className="user-modal__section">
                                <h3>{t('admin.activity')}</h3>
                                <p>
                                    <strong>{t('admin.timecreated')}:</strong>{' '}
                                    {formatDate(selectedUser?.created, { format: 'LLL' })}
                                </p>
                                <p>
                                    <strong>{t('admin.lastLogin')}:</strong>{' '}
                                    {formatDate(selectedUser?.lastLogin, { format: 'LLL' })}
                                </p>
                                <p>
                                    <strong>{t('admin.lastActivity')}:</strong>{' '}
                                    {formatDate(selectedUser?.lastActivity, { format: 'LLL' })}
                                </p>
                                <p>
                                    <strong>{t('settings.favoriteCharacter')}:</strong>{' '}
                                    {selectedUser?.favoriteCharacter || t('admin.unknown')}
                                </p>
                                <p>
                                    <strong>{t('admin.siteStatus')}:</strong>{' '}
                                    <span
                                        className={isOnline(selectedUser?.lastActivity) ? 'status-online' : 'status-offline'}>
                                        {isOnline(selectedUser?.lastActivity) ? t('admin.online') : t('admin.offline')}
                                    </span>
                                </p>
                            </div>
                            <div className="user-modal__section">
                                <h3>{t('admin.rolesSubscribtions')}</h3>
                                <p>
                                <strong>{t('admin.role')}:</strong>{' '}
                                    {formatRoles(selectedUser?.roles).join(', ')}
                                </p>
                                <p>
                                    <strong>{t('admin.email')}:</strong>{' '}
                                    {selectedUser?.email || t('admin.unknown')}
                                </p>
                                <p>
                                    <strong>{t('admin.emailVerified')}:</strong>{' '}
                                    {selectedUser?.emailVerified ? t('admin.yes') : t('admin.no')}
                                </p>
                                <p>
                                    <strong>{t('admin.subscribed')}:</strong>{' '}
                                    {selectedUser?.subscribed ? t('admin.yes') : t('admin.no')}
                                </p>
                                <p>
                                    <strong>{t('admin.language')}:</strong>{' '}
                                    {selectedUser?.language || 'EN'}
                                </p>
                            </div>
                            {selectedUser?.banStartDate && (
                                <div className="user-modal__section">
                                    <h3>{t('admin.banInfo')}</h3>
                                    <p>
                                        <strong>{t('admin.banStartDate')}:</strong>{' '}
                                        {formatDate(selectedUser.banStartDate, { format: 'LLL' })}
                                    </p>
                                    <p>
                                        <strong>{t('admin.banEndDate')}:</strong>{' '}
                                        {formatDate(selectedUser.banEndDate, { format: 'LLL' })}
                                    </p>
                                    <p>
                                        <strong>{t('admin.banReason')}:</strong>{' '}
                                        {selectedUser.banReason || t('admin.unknown')}
                                    </p>
                                </div>
                            )}
                            {selectedUser.profileData?.avatarfull && (
                                <div className="user-modal__section">
                                    <div className="user-steam-icon-wrapper">
                                        <span>{t('admin.steamIcon')}: </span>
                                        <div>
                                            <img
                                                src={selectedUser.profileData?.avatarfull}
                                                alt="Steam Icon"
                                                className="user-steam-icon"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Modal.Body>
                    <Modal.Footer style={{ borderTop: '1px solid var(--sp-border)' }}>
                        <button
                            onClick={handleBanButtonClick}
                            className="ban-button"
                        >
                            {t('admin.banUserButton')}
                        </button>
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    );
}

export default UsersTab;
